import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Building2, LayoutGrid, FilePlus, List,
  BarChart3, Plus, RefreshCw, Loader2, X, Users, UserPlus,
  Target, Layers, Wallet, TrendingUp, Search, Trophy, AlertCircle, Check, ChevronDown, CalendarDays,
  FileText, Link2, Trash2, Save, Edit3, Upload, Download, ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  getHeadcountOverview, getHeadcountSlots, getHeadcountRequests,
  createHeadcountRequest, approveHeadcountRequest, rejectHeadcountRequest,
  cancelHeadcountRequest, updateHeadcountSlotStatus, getMentionableUsers,
  getHeadcountApproverCandidates, importHeadcountSlots, cleanupHeadcountData, deleteHeadcountSlot, regenerateHeadcountCodes, getHeadcountRequestById,
} from '../api';
import {
  User, HeadcountOverview, HeadcountSlot, HeadcountRequest, TeamDoc,
  HC_TYPE_LABELS, HC_STATUS_LABELS, HC_TRANSITIONS, HC_REQUEST_STATUS_LABELS, HcType, HcStatus,
  HC_DEMAND_LABELS, HC_EMPLOYMENT_LABELS, URGENCY_LABELS,
} from '../types';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  initialTab?: TabKey;
  initialRequestId?: string;
  /** 从岗位详情跳来时按编制编号预置搜索，定位到该编制 */
  initialSlotSearch?: string;
  /** 点「对应岗位」跳岗位详情，未传时该列不可点 */
  onOpenPosition?: (positionId: string) => void;
}

type TabKey = 'overview' | 'requests' | 'list';

const TABS: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: '编制总览', icon: LayoutGrid },
  { key: 'list', label: '编制列表', icon: List },
  { key: 'requests', label: '编制审批', icon: FilePlus },
];

function normalizeTab(t?: string): TabKey {
  if (t === 'apply' || t === 'approval' || t === 'requests') return 'requests';
  if (t === 'list') return 'list';
  return 'overview';
}

const DONUT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#ec4899'];

function fmtMoney(n: number | null | undefined) {
  if (n == null) return '—';
  if (n >= 10000) return `¥${(n / 10000).toFixed(n >= 1000000 ? 0 : 1)}万`;
  return `¥${Math.round(n).toLocaleString()}`;
}

export default function EstablishmentPage({ currentUser, notificationBell, initialTab, initialRequestId, initialSlotSearch, onOpenPosition }: Props) {
  const isManager = ['admin', 'hr'].includes(currentUser.role);
  const canSeeCost = ['admin', 'hr'].includes(currentUser.role);
  const [tab, setTab] = useState<TabKey>(normalizeTab(initialTab));
  const [years, setYears] = useState<string[]>([String(new Date().getFullYear())]);
  const [department, setDepartment] = useState('');
  const yearParam = years.join(',');
  const yearLabel = years.length === 0 ? '全部年度' : years.length === 1 ? `${years[0]} 年度` : `${years.length} 个年度`;
  const [refreshKey, setRefreshKey] = useState(0);
  const [showApply, setShowApply] = useState(false);
  const [applyDraftId, setApplyDraftId] = useState<string | undefined>(undefined);

  const [overview, setOverview] = useState<HeadcountOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getHeadcountOverview({ year: yearParam || undefined, department: department || undefined });
      setOverview(data);
    } catch {
      toast.error('加载编制总览失败');
    } finally {
      setLoading(false);
    }
  }, [yearParam, department]);

  useEffect(() => { fetchOverview(); }, [fetchOverview, refreshKey]);

  const departments = useMemo(() => (overview?.byDepartment || []).map(d => d.department), [overview]);
  const refresh = () => setRefreshKey(k => k + 1);

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header + top tabs */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 pt-4 pb-0">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0 pl-10 md:pl-0">
            <Building2 size={20} className="text-indigo-500 shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-800 truncate">编制管理</h1>
              <p className="text-sm text-gray-400 hidden sm:block">动态编制 · 申请审批 · 数据复盘</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={department} onChange={e => setDepartment(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-200 text-sm bg-white text-gray-600">
              <option value="">全部部门</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <YearMultiSelect years={years} onChange={setYears} />
            <button onClick={() => setShowApply(true)}
              className="flex items-center gap-1.5 px-3 h-9 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">
              <Plus size={16} /><span className="hidden sm:inline">申请编制</span>
            </button>
            <button onClick={refresh} className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-indigo-600">
              <RefreshCw size={15} />
            </button>
            {notificationBell}
          </div>
        </div>

        {/* Top tabs */}
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={clsx('flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                  tab === t.key ? 'text-indigo-600 border-indigo-600' : 'text-gray-500 border-transparent hover:text-gray-700')}>
                <Icon size={15} />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {tab === 'overview' && (
          loading || !overview
            ? <Centered><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></Centered>
            : <OverviewTab overview={overview} canSeeCost={canSeeCost} yearLabel={yearLabel} />
        )}
        {tab === 'requests' && <RequestsTab currentUser={currentUser} isManager={isManager} canSeeCost={canSeeCost} refreshKey={refreshKey} onChanged={refresh}
          onResumeDraft={(id) => { setApplyDraftId(id); setShowApply(true); }} initialRequestId={initialRequestId} />}
        {tab === 'list' && <SlotListTab currentUser={currentUser} canSeeCost={canSeeCost} isManager={isManager} year={yearParam} department={department} refreshKey={refreshKey} onChanged={refresh}
          initialSearch={initialSlotSearch} onOpenPosition={onOpenPosition} />}
      </div>

      {showApply && (
        <ApplyModal currentUser={currentUser} canSeeCost={canSeeCost} departments={departments} draftId={applyDraftId}
          onClose={() => { setShowApply(false); setApplyDraftId(undefined); }}
          onCreated={() => { setShowApply(false); setApplyDraftId(undefined); refresh(); toast.success('编制申请已提交'); }} />
      )}
    </div>
  );
}

/* ============================ 编制总览 ============================ */
function OverviewTab({ overview, canSeeCost, yearLabel }: { overview: HeadcountOverview; canSeeCost: boolean; yearLabel: string }) {
  const s = overview.summary;
  // 五态口径：已使用 = Offer锁定 + 已占用，故拆成两张卡展示，占用率仍按「已使用」计
  const cards = [
    { label: '编制总数', value: s.total, sub: yearLabel, icon: Target, color: 'blue' },
    { label: '已占用', value: s.occupied, sub: `已使用 ${s.used} · ${s.fillRate}%`, icon: Users, color: 'blue' },
    { label: 'Offer锁定', value: s.reserved, sub: 'Offer 流转中', icon: Wallet, color: 'amber' },
    { label: '招聘中', value: s.recruiting, sub: '流水线在招', icon: UserPlus, color: 'emerald' },
    { label: '空闲编制', value: s.available, sub: '可被岗位绑定', icon: Layers, color: 'violet' },
  ];
  const maxDept = Math.max(1, ...overview.byDepartment.map(d => d.total));

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', bg(c.color))}>
                  <Icon size={18} className={tx(c.color)} />
                </div>
                <span className="text-[11px] text-gray-400">{c.sub}</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{c.value}</p>
              <p className="text-xs text-gray-500 mt-1">{c.label}</p>
            </div>
          );
        })}
        {/* Cost card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-rose-50">
              <Wallet size={18} className="text-rose-500" />
            </div>
          </div>
          {canSeeCost && overview.summary.cost ? (
            <>
              <p className="text-xl font-bold text-gray-900">{fmtMoney(overview.summary.cost.budget)}</p>
              <p className="text-xs text-gray-500 mt-1">人力成本预算</p>
            </>
          ) : (
            <>
              <p className="text-xl font-bold text-gray-300">—</p>
              <p className="text-xs text-gray-400 mt-1">人力成本（暂未开放）</p>
            </>
          )}
        </div>
      </div>

      {/* Trend + dept distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="编制使用趋势" subtitle="近 12 个月" icon={TrendingUp}>
          <LineTrend data={overview.trend} />
        </Panel>
        <Panel title="部门编制分布" subtitle="已占用 / Offer锁定 / 招聘中 / 空闲" icon={BarChart3}>
          <div className="space-y-3 mt-1">
            {overview.byDepartment.length === 0 && <Empty text="暂无编制数据" />}
            {overview.byDepartment.map(d => (
              <div key={d.department}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-gray-700">{d.department}</span>
                  <span className="text-gray-400">{d.total}</span>
                </div>
                <div className="h-5 rounded-md bg-gray-100 overflow-hidden flex">
                  <Seg w={d.occupied / maxDept} cls="bg-blue-500" />
                  <Seg w={d.reserved / maxDept} cls="bg-amber-500" />
                  <Seg w={d.recruiting / maxDept} cls="bg-emerald-500" />
                  <Seg w={d.available / maxDept} cls="bg-gray-300" />
                </div>
              </div>
            ))}
            <Legend items={[['已占用', 'bg-blue-500'], ['Offer锁定', 'bg-amber-500'], ['招聘中', 'bg-emerald-500'], ['空闲', 'bg-gray-300']]} />
          </div>
        </Panel>
      </div>

      {/* Donuts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="岗位职级分布" icon={Layers}><DonutBlock total={overview.summary.total} data={overview.byLevel} /></Panel>
        <Panel title="编制类型分布" icon={Layers}><DonutBlock total={overview.summary.total} data={overview.byType} /></Panel>
        <Panel title="编制状态分布" icon={Layers}><DonutBlock total={overview.summary.total} data={overview.byStatus} /></Panel>
      </div>

      {/* Cost ranking */}
      {overview.costRanking && overview.costRanking.length > 0 && (
        <Panel title="部门人均成本排名" subtitle={canSeeCost ? '人均年薪成本，从高到低' : '部门负责人视角：成本金额暂未开放，仅展示排名'} icon={Trophy}>
          <div className="mt-2 divide-y divide-gray-50">
            {overview.costRanking.map(r => (
              <div key={r.department} className="flex items-center justify-between py-2.5 text-sm">
                <div className="flex items-center gap-3">
                  <span className={clsx('w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                    r.rank === 1 ? 'bg-amber-100 text-amber-600' : r.rank === 2 ? 'bg-gray-100 text-gray-500' : r.rank === 3 ? 'bg-orange-100 text-orange-500' : 'bg-gray-50 text-gray-400')}>
                    {r.rank}
                  </span>
                  <span className="font-medium text-gray-700">{r.department}</span>
                  <span className="text-xs text-gray-400">在编 {r.headcount}</span>
                </div>
                <span className={clsx('font-semibold', canSeeCost ? 'text-gray-800' : 'text-gray-300')}>
                  {canSeeCost && r.perCapita != null ? `${fmtMoney(r.perCapita)}/人` : '—'}
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

/* ============================ 编制申请与审批（合并） ============================ */
function RequestsTab({ currentUser, isManager, canSeeCost, refreshKey, onChanged, onResumeDraft, initialRequestId }: {
  currentUser: User; isManager: boolean; canSeeCost: boolean; refreshKey: number; onChanged: () => void; onResumeDraft: (id: string) => void; initialRequestId?: string;
}) {
  const [reqs, setReqs] = useState<HeadcountRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<'mine' | 'all' | 'drafts'>('mine');
  const [drafts, setDrafts] = useState<HcDraft[]>([]);
  const [detailReq, setDetailReq] = useState<HeadcountRequest | null>(null);
  const [autoOpenId, setAutoOpenId] = useState<string | undefined>(initialRequestId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReqs(await getHeadcountRequests());
    } catch { toast.error('加载失败'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (scope === 'drafts') setDrafts(loadDrafts()); }, [scope]);

  // Auto-open detail modal when coming from Feishu notification link
  useEffect(() => {
    if (!autoOpenId || loading || reqs.length === 0) return;
    const found = reqs.find(r => r.id === autoOpenId);
    if (found) {
      setDetailReq(found);
      setAutoOpenId(undefined);
    }
  }, [autoOpenId, loading, reqs]);

  const doApprove = async (id: string, comment: string) => {
    try { const r = await approveHeadcountRequest(id, comment); toast.success(r.message || '已通过'); setDetailReq(null); load(); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || '操作失败'); }
  };
  const doReject = async (id: string, comment: string) => {
    try { await rejectHeadcountRequest(id, comment); toast.success('已驳回'); setDetailReq(null); load(); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || '操作失败'); }
  };
  const doCancel = async (id: string) => {
    try { await cancelHeadcountRequest(id); toast.success('已撤回'); setDetailReq(null); load(); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || '撤回失败'); }
  };

  const shown = scope === 'mine' ? reqs.filter(r => r.applicant_id === currentUser.id || r.pending_for_me) : reqs;
  const pendingForMeCount = reqs.filter(r => r.pending_for_me).length;
  const draftCount = loadDrafts().length;

  const scopes: { key: 'mine' | 'all' | 'drafts'; label: string }[] = [
    { key: 'mine', label: `我的申请${pendingForMeCount > 0 ? ` (待审批 ${pendingForMeCount})` : ''}` },
  ];
  scopes.push({ key: 'all', label: '全部申请' });
  scopes.push({ key: 'drafts', label: `草稿箱${draftCount > 0 ? ` (${draftCount})` : ''}` });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
          {scopes.map(s => (
            <button key={s.key} onClick={() => setScope(s.key)}
              className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium', scope === s.key ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500')}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      {loading && scope !== 'drafts' ? <Centered className="py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></Centered> : (
        <>
          {scope === 'drafts' ? (
            <>
              {drafts.length === 0 && <Empty text="没有草稿" />}
              {drafts.map(d => (
                <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-gray-900">{d.form.position_name || '未命名草稿'}</h3>
                      {d.form.department && <span className="text-[11px] text-gray-400">{d.form.department}</span>}
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">草稿</span>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      保存于 {(d.savedAt || '').slice(0, 16).replace('T', ' ')}
                      {d.form.headcount && ` · 数量 ${d.form.headcount}`}
                      {d.form.reason && ` · ${d.form.reason.slice(0, 40)}${d.form.reason.length > 40 ? '...' : ''}`}
                    </div>
                  </div>
                  <div className="shrink-0 flex gap-1.5">
                    <button onClick={() => onResumeDraft(d.id)}
                      className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1">
                      <Edit3 size={13} />继续编辑
                    </button>
                    <button onClick={() => { deleteDraft(d.id); setDrafts(loadDrafts()); toast.success('草稿已删除'); }}
                      className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-500 hover:text-rose-500 hover:bg-rose-50">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {shown.length === 0 && <Empty text="暂无申请记录" />}
              {shown.map(r => (
                <RequestRow key={r.id} r={r} canSeeCost={canSeeCost} currentUser={currentUser} onClick={() => setDetailReq(r)} />
              ))}
            </>
          )}
        </>
      )}
      {detailReq && (
        <RequestDetailModal r={detailReq} canSeeCost={canSeeCost} currentUser={currentUser}
          onClose={() => setDetailReq(null)}
          onApprove={(comment) => doApprove(detailReq.id, comment)}
          onReject={(comment) => doReject(detailReq.id, comment)}
          onCancel={() => doCancel(detailReq.id)} />
      )}
    </div>
  );
}

const URGENCY_COLOR: Record<string, string> = { low: 'text-gray-400', normal: 'text-gray-400', high: 'text-amber-500', urgent: 'text-rose-500' };

function RequestRow({ r, canSeeCost, currentUser, onClick }: {
  r: HeadcountRequest; canSeeCost: boolean; currentUser: User; onClick: () => void;
}) {
  const statusColor: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-600', approved: 'bg-emerald-50 text-emerald-600',
    rejected: 'bg-rose-50 text-rose-600', cancelled: 'bg-gray-100 text-gray-400',
  };
  const canApproveThis = r.pending_for_me && r.status === 'pending';
  const canCancelThis = r.applicant_id === currentUser.id && r.status === 'pending';

  return (
    <div onClick={onClick} className="bg-white rounded-xl border border-gray-200 p-3.5 hover:border-indigo-300 hover:shadow-sm cursor-pointer transition-all">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-gray-900 truncate">{r.position_name}</span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-500 shrink-0">×{r.headcount}</span>
          <span className={clsx('text-[11px] px-1.5 py-0.5 rounded shrink-0', statusColor[r.status])}>{HC_REQUEST_STATUS_LABELS[r.status]}</span>
          {r.pending_for_me && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 shrink-0 animate-pulse">待我审批</span>}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-400 shrink-0">
          <span>{r.department}</span>
          <span>·</span>
          <span className={URGENCY_COLOR[r.urgency]}>{URGENCY_LABELS[r.urgency] || r.urgency}</span>
          <span>·</span>
          <span>{r.applicant_name || '未知'}</span>
          <span>·</span>
          <span>{(r.created_at || '').slice(0, 10)}</span>
          {(canApproveThis || canCancelThis) && <ChevronRight size={14} className="text-indigo-400" />}
        </div>
      </div>
    </div>
  );
}

function RequestDetailModal({ r, canSeeCost, currentUser, onClose, onApprove, onReject, onCancel }: {
  r: HeadcountRequest; canSeeCost: boolean; currentUser: User;
  onClose: () => void;
  onApprove: (comment: string) => void;
  onReject: (comment: string) => void;
  onCancel: () => void;
}) {
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState(false);
  const LEVEL_LABEL = { 1: '一级(HR)', 2: '二级(业务)', 3: '三级(CEO)' };
  const statusColor: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-600', approved: 'bg-emerald-50 text-emerald-600',
    rejected: 'bg-rose-50 text-rose-600', cancelled: 'bg-gray-100 text-gray-400',
  };
  const canApproveThis = r.pending_for_me && r.status === 'pending';
  const canCancelThis = r.applicant_id === currentUser.id && r.status === 'pending';

  const handleApprove = () => { setActing(true); onApprove(comment); };
  const handleReject = () => { setActing(true); onReject(comment); };

  const levels = [
    { num: 1, name: r.approver_l1_name, status: r.l1_status, comment: r.l1_comment, at: r.l1_at },
    { num: 2, name: r.approver_l2_name, status: r.l2_status, comment: r.l2_comment, at: r.l2_at },
    { num: 3, name: r.approver_l3_name, status: r.l3_status, comment: r.l3_comment, at: r.l3_at },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-gray-800">编制申请详情</h3>
            <span className={clsx('text-xs px-2 py-0.5 rounded', statusColor[r.status])}>{HC_REQUEST_STATUS_LABELS[r.status]}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* 基本信息 */}
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">{r.position_name}</h2>
            <span className="text-sm px-2 py-0.5 rounded bg-indigo-50 text-indigo-500">×{r.headcount}</span>
            {r.demand_type && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">{HC_DEMAND_LABELS[r.demand_type]}</span>}
            {r.employment_type && <span className="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-500">{HC_EMPLOYMENT_LABELS[r.employment_type]}</span>}
            <span className={clsx('text-[11px] px-1.5 py-0.5 rounded', URGENCY_COLOR[r.urgency], 'bg-gray-50')}>{URGENCY_LABELS[r.urgency] || r.urgency}</span>
          </div>

          {/* 关键信息网格 */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
            <div><span className="text-gray-400">需求部门</span><br/><span className="text-gray-700 font-medium">{r.department || '—'}</span></div>
            <div><span className="text-gray-400">发起人</span><br/><span className="text-gray-700 font-medium">{r.applicant_name || '—'}</span></div>
            {r.requester_name && <div><span className="text-gray-400">提出人</span><br/><span className="text-gray-700 font-medium">{r.requester_name}</span></div>}
            <div><span className="text-gray-400">年度</span><br/><span className="text-gray-700 font-medium">{r.year || '—'}</span></div>
            {r.job_level && <div><span className="text-gray-400">职级</span><br/><span className="text-gray-700 font-medium">{r.job_level}</span></div>}
            {r.work_location && <div><span className="text-gray-400">工作地点</span><br/><span className="text-gray-700 font-medium">{r.work_location}</span></div>}
            {r.expected_date && <div><span className="text-gray-400">预期到岗</span><br/><span className="text-gray-700 font-medium">{r.expected_date}</span></div>}
            {r.report_to && <div><span className="text-gray-400">汇报对象</span><br/><span className="text-gray-700 font-medium">{r.report_to}</span></div>}
            {r.mentor && <div><span className="text-gray-400">导师</span><br/><span className="text-gray-700 font-medium">{r.mentor}</span></div>}
            {canSeeCost && r.budget_annual != null && r.budget_annual > 0 && <div><span className="text-gray-400">预算/人</span><br/><span className="text-gray-700 font-medium">{fmtMoney(r.budget_annual)}</span></div>}
            {r.approver_l1_name && <div><span className="text-gray-400">一级审批人(HR)</span><br/><span className="text-gray-700 font-medium">{r.approver_l1_name}</span></div>}
            {r.approver_l2_name && <div><span className="text-gray-400">二级审批人(业务)</span><br/><span className="text-gray-700 font-medium">{r.approver_l2_name}</span></div>}
            {r.approver_l3_name && <div><span className="text-gray-400">三级审批人(CEO)</span><br/><span className="text-gray-700 font-medium">{r.approver_l3_name}</span></div>}
            {r.interviewer_first && <div><span className="text-gray-400">一面面试官</span><br/><span className="text-gray-700 font-medium">{r.interviewer_first}</span></div>}
            {r.interviewer_second && <div><span className="text-gray-400">二面面试官</span><br/><span className="text-gray-700 font-medium">{r.interviewer_second}</span></div>}
            {r.interviewer_final && <div><span className="text-gray-400">终面面试官</span><br/><span className="text-gray-700 font-medium">{r.interviewer_final}</span></div>}
          </div>

          {/* 详情区块 */}
          {r.reason && <DetailBlock label="编制需求原因" text={r.reason} />}
          {r.goal_90d && <DetailBlock label="未来90天核心交付目标" text={r.goal_90d} />}
          {r.talent_profile && <DetailBlock label="人才画像" text={r.talent_profile} />}

          {/* 团队文档 */}
          {r.team_docs && r.team_docs.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1.5">现有团队文档</div>
              <div className="flex flex-wrap gap-1.5">
                {r.team_docs.map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-sky-50 text-sky-600 hover:bg-sky-100">
                    <FileText size={12} className="shrink-0" />
                    <span className="truncate max-w-[200px]">{d.title || d.url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* 审批进度 */}
          {r.status !== 'cancelled' && (
            <div className="pt-3 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-2">审批进度</div>
              <div className="space-y-2">
                {levels.map(l => (
                  <div key={l.num} className="flex items-start gap-2.5">
                    <div className={clsx('w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0',
                      l.status === 'approved' ? 'bg-emerald-500 text-white' :
                      l.status === 'rejected' ? 'bg-rose-500 text-white' :
                      r.current_level === l.num ? 'bg-amber-400 text-white' : 'bg-gray-200 text-gray-400')}>
                      {l.status === 'approved' ? <Check size={14} /> : l.status === 'rejected' ? <X size={14} /> : l.num}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">{LEVEL_LABEL[l.num as 1|2|3]}</span>
                        <span className="text-xs text-gray-400">{l.name || '未指定'}</span>
                        {l.status === 'approved' && <span className="text-[10px] text-emerald-500">已通过</span>}
                        {l.status === 'rejected' && <span className="text-[10px] text-rose-500">已驳回</span>}
                        {r.current_level === l.num && l.status === 'pending' && <span className="text-[10px] text-amber-500">待审批</span>}
                      </div>
                      {l.at && <div className="text-[10px] text-gray-400">{(l.at || '').slice(0, 16).replace('T', ' ')}</div>}
                      {l.comment && <div className="text-xs text-gray-500 mt-0.5 bg-gray-50 rounded px-2 py-1">{l.comment}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 审批操作区 */}
          {canApproveThis && (
            <div className="pt-4 border-t-2 border-indigo-100">
              <div className="text-sm font-medium text-gray-700 mb-2">审批操作</div>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="审批备注（可选）"
                className="w-full min-h-[4rem] px-3 py-2 rounded-lg border border-gray-200 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 mb-3"
              />
              <div className="flex gap-3">
                <button onClick={handleApprove} disabled={acting}
                  className="flex-1 py-2.5 rounded-lg bg-emerald-500 text-white font-medium text-sm hover:bg-emerald-600 disabled:opacity-60 flex items-center justify-center gap-1.5">
                  <Check size={16} />通过审批
                </button>
                <button onClick={handleReject} disabled={acting}
                  className="flex-1 py-2.5 rounded-lg bg-rose-500 text-white font-medium text-sm hover:bg-rose-600 disabled:opacity-60 flex items-center justify-center gap-1.5">
                  <X size={16} />驳回申请
                </button>
              </div>
            </div>
          )}

          {/* 撤回按钮 */}
          {canCancelThis && !canApproveThis && (
            <div className="pt-4 border-t border-gray-100">
              <button onClick={onCancel}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-500 text-sm hover:bg-gray-50">撤回申请</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================ 编制列表 ============================ */
function SlotListTab({ currentUser, canSeeCost, isManager, year, department, refreshKey, onChanged, initialSearch, onOpenPosition }: {
  currentUser: User; canSeeCost: boolean; isManager: boolean; year: string; department: string; refreshKey: number; onChanged: () => void;
  initialSearch?: string; onOpenPosition?: (positionId: string) => void;
}) {
  const [slots, setSlots] = useState<HeadcountSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'' | HcStatus>('');
  const [search, setSearch] = useState(initialSearch || '');
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [showImport, setShowImport] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [detailReq, setDetailReq] = useState<HeadcountRequest | null>(null);
  const [detailSlot, setDetailSlot] = useState<HeadcountSlot | null>(null);
  const rangeActive = !!(range.from || range.to);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { department: department || undefined, status: status || undefined, search: search || undefined };
      // 指定创建时间范围时按时间跨年度拉取（忽略右上角年度）；否则按所选年度
      if (range.from || range.to) { params.created_from = range.from || undefined; params.created_to = range.to || undefined; }
      else { params.year = year || undefined; }
      setSlots(await getHeadcountSlots(params));
    } catch { toast.error('加载失败'); } finally { setLoading(false); }
  }, [year, department, status, search, range]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  const changeStatus = async (id: string, st: HcStatus) => {
    try { await updateHeadcountSlotStatus(id, st); toast.success('状态已更新'); load(); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.message || '操作失败'); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.size === slots.length ? new Set() : new Set(slots.map(s => s.id)));
  };
  const batchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`确定删除选中的 ${selectedIds.size} 条编制吗？此操作不可撤销。`)) return;
    setDeleting(true);
    try {
      let ok = 0, fail = 0;
      for (const id of Array.from(selectedIds)) {
        try { await deleteHeadcountSlot(id); ok++; } catch { fail++; }
      }
      toast.success(`删除完成：成功 ${ok} 条${fail > 0 ? `，失败 ${fail} 条` : ''}`);
      setSelectedIds(new Set());
      load(); onChanged();
    } catch { toast.error('删除失败'); }
    finally { setDeleting(false); }
  };

  const openSlotDetail = async (slot: HeadcountSlot) => {
    if (slot.request_id) {
      try {
        const req = await getHeadcountRequestById(slot.request_id);
        if (req) { setDetailReq(req); return; }
      } catch { /* fall through to slot detail */ }
    }
    setDetailSlot(slot);
  };

  const filters: { key: '' | HcStatus; label: string }[] = [
    { key: '', label: '全部' }, { key: 'AVAILABLE', label: '空闲' }, { key: 'RECRUITING', label: '招聘中' },
    { key: 'RESERVED', label: 'Offer锁定' }, { key: 'OCCUPIED', label: '已占用' }, { key: 'CLOSED', label: '已关闭' },
  ];

  return (
    <>
    <div className="bg-white rounded-2xl border border-gray-200">
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap border-b border-gray-100">
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {filters.map(f => (
            <button key={f.key} onClick={() => setStatus(f.key)}
              className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap', status === f.key ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50')}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter range={range} onChange={setRange} />
          {isManager && (
            <button onClick={() => setShowImport(true)}
              className="flex items-center gap-1.5 px-3 h-8 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50">
              <Upload size={14} />导入
            </button>
          )}
          {canSeeCost && (
            <button onClick={async () => {
              if (!window.confirm('将删除除"三维渲染"外的所有编制和审批记录，确定吗？')) return;
              try {
                const r = await cleanupHeadcountData('三维渲染');
                toast.success(r.message || '清理完成');
                load(); onChanged();
              } catch (e: any) { toast.error(e?.response?.data?.message || '清理失败'); }
            }}
              className="flex items-center gap-1.5 px-3 h-8 text-sm font-medium text-rose-600 border border-rose-200 rounded-lg hover:bg-rose-50">
              <Trash2 size={14} />清理测试数据
            </button>
          )}
          {canSeeCost && (
            <button onClick={async () => {
              try {
                const r = await regenerateHeadcountCodes();
                toast.success(r.message || '重编完成');
                load();
              } catch (e: any) { toast.error(e?.response?.data?.message || '重编失败'); }
            }}
              className="flex items-center gap-1.5 px-3 h-8 text-sm font-medium text-amber-600 border border-amber-200 rounded-lg hover:bg-amber-50">
              <RefreshCw size={14} />重编编号
            </button>
          )}
          {canSeeCost && selectedIds.size > 0 && (
            <button onClick={batchDelete} disabled={deleting}
              className="flex items-center gap-1.5 px-3 h-8 text-sm font-medium text-white bg-rose-500 rounded-lg hover:bg-rose-600 disabled:opacity-60">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              删除选中({selectedIds.size})
            </button>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索岗位/编号..."
              className="h-8 pl-8 pr-3 rounded-lg border border-gray-200 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
        </div>
      </div>
      {rangeActive && (
        <div className="px-4 py-2 text-[11px] text-indigo-500 bg-indigo-50/50 border-b border-indigo-100 flex items-center gap-1.5">
          <CalendarDays size={12} /> 已按创建时间 {range.from || '不限'} ~ {range.to || '不限'} 拉取（跨年度），共 {slots.length} 条
        </div>
      )}
      {loading ? <Centered className="py-16"><Loader2 className="w-7 h-7 animate-spin text-indigo-500" /></Centered> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                {canSeeCost && <th className="px-3 py-2.5 w-10">
                  <input type="checkbox" checked={slots.length > 0 && selectedIds.size === slots.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400 cursor-pointer" />
                </th>}
                <th className="px-4 py-2.5 font-medium">编号</th>
                <th className="px-4 py-2.5 font-medium">岗位</th>
                <th className="px-4 py-2.5 font-medium">对应岗位</th>
                <th className="px-4 py-2.5 font-medium">部门</th>
                <th className="px-4 py-2.5 font-medium">职级</th>
                <th className="px-4 py-2.5 font-medium">用工类型</th>
                <th className="px-4 py-2.5 font-medium">状态</th>
                <th className="px-4 py-2.5 font-medium">负责人</th>
                <th className="px-4 py-2.5 font-medium">发起时间</th>
                <th className="px-4 py-2.5 font-medium">关闭时间</th>
                {canSeeCost && <th className="px-4 py-2.5 font-medium text-right">预算(年薪)</th>}
                {isManager && <th className="px-4 py-2.5 font-medium">操作</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {slots.length === 0 && (
                <tr><td colSpan={11 + (canSeeCost ? 1 : 0) + (isManager ? 1 : 0)} className="px-4 py-12 text-center text-gray-400">暂无编制</td></tr>
              )}
              {slots.map(s => (
                <tr key={s.id} className="hover:bg-gray-50">
                  {canSeeCost && <td className="px-3 py-3">
                    <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)}
                      className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-400 cursor-pointer" />
                  </td>}
                  <td className="px-4 py-3 text-sm text-gray-500 tabular-nums">{s.code}</td>
                  <td className="px-4 py-3 font-medium">
                    <button onClick={() => openSlotDetail(s)} className="text-indigo-600 hover:text-indigo-700 hover:underline">
                      {s.position_name}
                    </button>
                  </td>
                  {/* 联动：绑定的岗位可点击跳岗位详情（旧 position_id 字段仅作兼容展示，不可点） */}
                  <td className="px-4 py-3">
                    {s.bound_position_id && onOpenPosition ? (
                      <button onClick={() => onOpenPosition(s.bound_position_id!)}
                        className="text-indigo-600 hover:text-indigo-700 hover:underline">
                        {s.bound_position_name}
                      </button>
                    ) : (
                      <span className="text-gray-400">{s.bound_position_name || (s as any).position_link_name || '未绑定'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{s.department || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{s.job_level || '-'}</td>
                  <td className="px-4 py-3 text-gray-500">{(HC_EMPLOYMENT_LABELS as Record<string, string>)[(s as any).employment_type || (s as any).req_employment_type || 'fulltime'] || '正式'}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                  <td className="px-4 py-3 text-gray-600">{s.candidate_name || s.owner_name || '-'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{(s.created_at || '').slice(0, 10) || '-'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{s.closed_at ? s.closed_at.slice(0, 10) : '-'}</td>
                  {canSeeCost && <td className="px-4 py-3 text-right text-gray-700">{s.budget_annual ? fmtMoney(s.budget_annual) : '-'}</td>}
                  {isManager && (
                    <td className="px-4 py-3">
                      {/* 只列出当前状态的合法下一态；RESERVED / OCCUPIED 的流转由 Offer 链路驱动，此处不提供手工项 */}
                      {HC_TRANSITIONS[s.status]?.length ? (
                        <select value="" onChange={e => e.target.value && changeStatus(s.id, e.target.value as HcStatus)}
                          className="h-7 px-1.5 rounded border border-gray-200 text-xs bg-white text-gray-600">
                          <option value="">{HC_STATUS_LABELS[s.status]} · 变更为…</option>
                          {HC_TRANSITIONS[s.status].map(next => (
                            <option key={next} value={next}>{HC_STATUS_LABELS[next]}</option>
                          ))}
                        </select>
                      ) : <span className="text-xs text-gray-400">{HC_STATUS_LABELS[s.status]}</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    {showImport && <ImportSlotsModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); onChanged(); }} />}
    {detailReq && (
      <RequestDetailModal r={detailReq} canSeeCost={canSeeCost} currentUser={currentUser}
        onClose={() => setDetailReq(null)}
        onApprove={() => {}}
        onReject={() => {}}
        onCancel={() => {}} />
    )}
    {detailSlot && (
      <SlotDetailModal s={detailSlot} canSeeCost={canSeeCost} onClose={() => setDetailSlot(null)} />
    )}
    </>
  );
}

/* ============================ 编制详情弹窗（手动创建/导入） ============================ */
function SlotDetailModal({ s, canSeeCost, onClose }: { s: HeadcountSlot; canSeeCost: boolean; onClose: () => void }) {
  const empType = (s as any).employment_type || (s as any).req_employment_type || 'fulltime';
  const empLabel = (HC_EMPLOYMENT_LABELS as Record<string, string>)[empType] || '正式';
  const hcTypeLabel = HC_TYPE_LABELS[s.hc_type] || '-';
  const demandLabel = (s as any).demand_type ? (HC_DEMAND_LABELS as Record<string, string>)[(s as any).demand_type] : null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <FileText size={18} className="text-indigo-500" />
            编制详情 · {s.code}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={s.status} />
            <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-600">{empLabel}</span>
            <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 text-gray-500">{hcTypeLabel}</span>
            {demandLabel && <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-600">{demandLabel}</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 text-sm">
            <div><span className="text-gray-400">编号</span><br/><span className="text-gray-700 font-medium tabular-nums">{s.code}</span></div>
            <div><span className="text-gray-400">岗位名称</span><br/><span className="text-gray-700 font-medium">{s.position_name}</span></div>
            <div><span className="text-gray-400">部门</span><br/><span className="text-gray-700 font-medium">{s.department || '—'}</span></div>
            <div><span className="text-gray-400">职级</span><br/><span className="text-gray-700 font-medium">{s.job_level || '—'}</span></div>
            <div><span className="text-gray-400">用工类型</span><br/><span className="text-gray-700 font-medium">{empLabel}</span></div>
            <div><span className="text-gray-400">编制类型</span><br/><span className="text-gray-700 font-medium">{hcTypeLabel}</span></div>
            {demandLabel && <div><span className="text-gray-400">需求类型</span><br/><span className="text-gray-700 font-medium">{demandLabel}</span></div>}
            <div><span className="text-gray-400">年度</span><br/><span className="text-gray-700 font-medium">{s.year || '—'}</span></div>
            <div><span className="text-gray-400">负责人</span><br/><span className="text-gray-700 font-medium">{s.owner_name || '—'}</span></div>
            {s.candidate_name && <div><span className="text-gray-400">入职候选人</span><br/><span className="text-gray-700 font-medium">{s.candidate_name}</span></div>}
            {canSeeCost && s.budget_annual != null && s.budget_annual > 0 && <div><span className="text-gray-400">预算/年</span><br/><span className="text-gray-700 font-medium">{fmtMoney(s.budget_annual)}</span></div>}
            {canSeeCost && s.actual_salary != null && s.actual_salary > 0 && <div><span className="text-gray-400">实际年薪</span><br/><span className="text-gray-700 font-medium">{fmtMoney(s.actual_salary)}</span></div>}
            <div><span className="text-gray-400">创建时间</span><br/><span className="text-gray-700 font-medium">{(s.created_at || '').slice(0, 16) || '—'}</span></div>
            {s.closed_at && <div><span className="text-gray-400">关闭时间</span><br/><span className="text-gray-700 font-medium">{s.closed_at.slice(0, 16)}</span></div>}
          </div>
          {s.notes && (
            <div>
              <span className="text-gray-400 text-sm">备注</span>
              <p className="mt-1 text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{s.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================ 导入编制弹窗 ============================ */
const SLOT_IMPORT_COLUMNS = [
  { key: 'position_name', label: '岗位名称', required: true, desc: '如：算法工程师' },
  { key: 'department', label: '部门', required: false, desc: '如：研发部' },
  { key: 'job_level', label: '职级', required: false, desc: '如：P6 / 高级' },
  { key: 'hc_type', label: '编制类型', required: false, desc: 'new=新增 / replace=替换 / temp=临时' },
  { key: 'employment_type', label: '用工类型', required: false, desc: 'fulltime=正式 / intern=实习 / outsource=外包 / consultant=顾问 / temp=临时工' },
  { key: 'status', label: '状态', required: false, desc: 'AVAILABLE=空闲 / CLOSED=已关闭（其余状态由绑定与 Offer 链路驱动，不可导入）' },
  { key: 'budget_annual', label: '预算(年薪/元)', required: false, desc: '如：300000' },
  { key: 'year', label: '年度', required: false, desc: '如：2026，默认当前年' },
  { key: 'notes', label: '备注', required: false, desc: '任意文本' },
];

function ImportSlotsModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<any[]>([]);
  const [fileName, setFileName] = useState('');

  const downloadTemplate = () => {
    const header = SLOT_IMPORT_COLUMNS.map(c => c.label).join(',');
    const example = SLOT_IMPORT_COLUMNS.map(c => {
      if (c.key === 'position_name') return '算法工程师';
      if (c.key === 'department') return '研发部';
      if (c.key === 'job_level') return 'P6';
      if (c.key === 'hc_type') return 'new';
      if (c.key === 'employment_type') return 'fulltime';
      if (c.key === 'status') return 'AVAILABLE';
      if (c.key === 'budget_annual') return '300000';
      if (c.key === 'year') return '2026';
      if (c.key === 'notes') return '示例数据行';
      return '';
    }).join(',');
    const csv = '\uFEFF' + header + '\n' + example + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '编制导入模板.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];
    // 去掉 BOM
    lines[0] = lines[0].replace(/^\uFEFF/, '');
    const headers = parseCSVLine(lines[0]);
    const colMap: Record<string, number> = {};
    SLOT_IMPORT_COLUMNS.forEach(col => {
      const idx = headers.findIndex(h => h.trim() === col.label || h.trim() === col.key);
      if (idx >= 0) colMap[col.key] = idx;
    });
    const rows: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = parseCSVLine(lines[i]);
      const row: any = {};
      for (const col of SLOT_IMPORT_COLUMNS) {
        if (colMap[col.key] != null) row[col.key] = (cells[colMap[col.key]] || '').trim();
      }
      rows.push(row);
    }
    return rows;
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let cur = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuote) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQuote = false;
        else cur += ch;
      } else {
        if (ch === '"') inQuote = true;
        else if (ch === ',') { result.push(cur); cur = ''; }
        else cur += ch;
      }
    }
    result.push(cur);
    return result;
  };

  const handleFile = (file: File) => {
    setParsing(true);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCSV(String(reader.result));
        setPreview(rows);
        if (rows.length === 0) toast.error('未解析到有效数据行');
        else toast.success(`解析到 ${rows.length} 行数据`);
      } catch { toast.error('CSV 解析失败'); }
      finally { setParsing(false); }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const doImport = async () => {
    if (preview.length === 0) { toast.error('没有数据可导入'); return; }
    setImporting(true);
    try {
      const result = await importHeadcountSlots(preview);
      if (result.errors?.length) {
        toast.error(`导入完成：成功 ${result.imported} 条，失败 ${result.errors.length} 条`);
      } else {
        toast.success(`成功导入 ${result.imported} 条编制`);
      }
      onDone();
    } catch (e: any) { toast.error(e?.response?.data?.message || '导入失败'); }
    finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2"><Upload size={18} className="text-indigo-500" />批量导入编制</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* 说明 + 下载模板 */}
          <div className="bg-indigo-50/50 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">第一步：下载 CSV 模板</p>
                <p className="text-xs text-gray-400 mt-0.5">按模板格式填写编制数据，仅「岗位名称」为必填</p>
              </div>
              <button onClick={downloadTemplate}
                className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
                <Download size={15} />下载模板
              </button>
            </div>
            <div className="pt-2 border-t border-indigo-100">
              <p className="text-xs text-gray-500 mb-1">字段说明：</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500">
                {SLOT_IMPORT_COLUMNS.map(c => (
                  <div key={c.key} className="flex items-center gap-1">
                    <span className="font-medium text-gray-700">{c.label}</span>
                    {c.required && <span className="text-rose-500">*</span>}
                    <span className="text-gray-400">— {c.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 上传文件 */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">第二步：上传填写好的 CSV 文件</p>
            <label className="block border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors">
              <input type="file" accept=".csv,text/csv" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
              {parsing ? (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <Loader2 size={24} className="animate-spin" />
                  <span className="text-sm">解析中...</span>
                </div>
              ) : fileName ? (
                <div className="text-sm text-gray-600">
                  <FileText size={24} className="mx-auto mb-1 text-indigo-400" />
                  {fileName}
                  <span className="text-gray-400 ml-2">（点击重新选择）</span>
                </div>
              ) : (
                <div className="text-gray-400">
                  <Upload size={24} className="mx-auto mb-2" />
                  <span className="text-sm">点击选择 CSV 文件</span>
                </div>
              )}
            </label>
          </div>

          {/* 预览 */}
          {preview.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">预览（前 5 行）</p>
              <div className="overflow-x-auto rounded-lg border border-gray-100">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-400">#</th>
                      {SLOT_IMPORT_COLUMNS.map(c => (
                        <th key={c.key} className="px-3 py-2 text-left text-gray-500 whitespace-nowrap">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {preview.slice(0, 5).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                        {SLOT_IMPORT_COLUMNS.map(c => (
                          <td key={c.key} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[120px] truncate">{row[c.key] || '-'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-1">共 {preview.length} 行数据</p>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">取消</button>
          <button onClick={doImport} disabled={importing || preview.length === 0}
            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-1.5">
            {importing && <Loader2 size={14} className="animate-spin" />}
            {importing ? '导入中...' : `导入 ${preview.length} 条`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================ 申请弹窗 ============================ */
const WORK_LOCATION_PRESETS = ['北京', '上海', '广州', '深圳', '杭州', '成都', '武汉', '南京', '远程'];

const DRAFT_KEY = 'hc_drafts';

interface HcDraft {
  id: string;
  form: any;
  teamDocs: TeamDoc[];
  savedAt: string;
}

function loadDrafts(): HcDraft[] {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '[]'); } catch { return []; }
}
function saveDrafts(drafts: HcDraft[]) {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(drafts));
}
function upsertDraft(form: any, teamDocs: TeamDoc[]): string {
  const drafts = loadDrafts();
  const id = form._draftId || `draft_${Date.now()}`;
  const idx = drafts.findIndex(d => d.id === id);
  const entry: HcDraft = { id, form: { ...form, _draftId: id }, teamDocs, savedAt: new Date().toISOString() };
  if (idx >= 0) drafts[idx] = entry; else drafts.unshift(entry);
  saveDrafts(drafts);
  return id;
}
function deleteDraft(id: string) {
  saveDrafts(loadDrafts().filter(d => d.id !== id));
}

function ApplyModal({ currentUser, canSeeCost, departments, onClose, onCreated, draftId }: {
  currentUser: User; canSeeCost: boolean; departments: string[]; onClose: () => void; onCreated: () => void; draftId?: string;
}) {
  const [form, setForm] = useState<Record<string, any>>(() => {
    const base = {
      demand_type: 'business', employment_type: 'fulltime', headcount: 1, urgency: 'normal',
      position_name: '', department: '', mentor: '', report_to: '', work_location: '',
      budget_annual: '', expected_date: '', job_level: '',
      interviewer_first: '', interviewer_second: '', interviewer_final: '',
      reason: '', goal_90d: '', talent_profile: '',
      requester_id: currentUser.id,
      approver_l1_id: '', approver_l2_id: '', approver_l3_id: '',
      year: String(new Date().getFullYear()),
    };
    if (draftId) {
      const d = loadDrafts().find(d => d.id === draftId);
      if (d) return { ...base, ...d.form, _draftId: draftId };
    }
    return base;
  });
  const [teamDocs, setTeamDocs] = useState<TeamDoc[]>(() => {
    if (draftId) {
      const d = loadDrafts().find(d => d.id === draftId);
      if (d && d.teamDocs?.length) return d.teamDocs;
    }
    return [{ title: '', url: '' }];
  });
  const [users, setUsers] = useState<User[]>([]);
  const [approverCandidates, setApproverCandidates] = useState<{ l1: User[]; l2: User[]; l3: User[] }>({ l1: [], l2: [], l3: [] });
  const [submitting, setSubmitting] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  useEffect(() => {
    getMentionableUsers()
      .then((list: User[]) => setUsers((list || []).filter(u => u.role !== 'referrer')))
      .catch(() => setUsers([]));
    getHeadcountApproverCandidates()
      .then((data: { l1: User[]; l2: User[]; l3: User[] }) => setApproverCandidates(data))
      .catch(() => setApproverCandidates({ l1: [], l2: [], l3: [] }));
  }, []);

  // 自动保存草稿（防抖 2s）
  const autoSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const hasContent = form.position_name || form.department || form.reason || form.goal_90d || form.talent_profile;
    if (!hasContent) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      const id = upsertDraft(form, teamDocs);
      setForm(f => ({ ...f, _draftId: id }));
      setDraftSavedAt(new Date().toLocaleTimeString());
    }, 2000);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [form, teamDocs]);
  const setDoc = (i: number, k: keyof TeamDoc, v: string) =>
    setTeamDocs(docs => docs.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const addDoc = () => setTeamDocs(docs => [...docs, { title: '', url: '' }]);
  const removeDoc = (i: number) => setTeamDocs(docs => docs.length <= 1 ? [{ title: '', url: '' }] : docs.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!form.position_name.trim()) { toast.error('请填写岗位名称'); return; }
    if (!form.department.trim()) { toast.error('请选择所属部门'); return; }
    if (!form.approver_l1_id || !form.approver_l2_id || !form.approver_l3_id) { toast.error('请选择三级审批人'); return; }
    const docs = teamDocs.map(d => ({ title: d.title.trim(), url: d.url.trim() })).filter(d => d.url);
    setSubmitting(true);
    try {
      await createHeadcountRequest({ ...form, headcount: Number(form.headcount) || 1, budget_annual: Number(form.budget_annual) || 0, team_docs: docs });
      if (form._draftId) deleteDraft(form._draftId);
      onCreated();
    } catch (e: any) { toast.error(e?.response?.data?.message || '提交失败'); }
    finally { setSubmitting(false); }
  };

  const saveDraftManually = () => {
    const id = upsertDraft(form, teamDocs);
    setForm(f => ({ ...f, _draftId: id }));
    setDraftSavedAt(new Date().toLocaleTimeString());
    toast.success('草稿已保存');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100 z-10">
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2"><FilePlus size={18} className="text-indigo-500" />{draftId ? '继续编辑草稿' : '申请编制'}</h3>
          <div className="flex items-center gap-2">
            {draftSavedAt && <span className="text-[11px] text-gray-400">草稿已保存 {draftSavedAt}</span>}
            <button onClick={saveDraftManually} className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"><Save size={13} />保存草稿</button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {/* 基本信息 */}
          <SectionTitle icon={Target} text="基本信息" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="需求类型" required>
              <select value={form.demand_type} onChange={e => set('demand_type', e.target.value)} className={inputCls}>
                {Object.entries(HC_DEMAND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="编制类型" required>
              <select value={form.employment_type} onChange={e => set('employment_type', e.target.value)} className={inputCls}>
                {Object.entries(HC_EMPLOYMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="数量" required>
              <input type="number" min={1} value={form.headcount} onChange={e => set('headcount', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="岗位名称" required>
              <input value={form.position_name} onChange={e => set('position_name', e.target.value)} className={inputCls} placeholder="如：算法工程师" />
            </Field>
            <Field label="所属部门" required>
              <input list="hc-dept-list" value={form.department} onChange={e => set('department', e.target.value)} className={inputCls} placeholder="选择或输入部门" />
              <datalist id="hc-dept-list">{departments.map(d => <option key={d} value={d} />)}</datalist>
            </Field>
            <Field label="需求提出人" required>
              <select value={form.requester_id} onChange={e => set('requester_id', e.target.value)} className={inputCls}>
                {!users.some(u => u.id === currentUser.id) && <option value={currentUser.id}>{currentUser.display_name || currentUser.username}（我）</option>}
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.display_name || u.username}{u.id === currentUser.id ? '（我）' : ''}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="紧急程度">
              <select value={form.urgency} onChange={e => set('urgency', e.target.value)} className={inputCls}>
                <option value="low">不急</option>
                <option value="normal">正常</option>
                <option value="high">紧急</option>
                <option value="urgent">加急</option>
              </select>
            </Field>
            <Field label="职级">
              <input value={form.job_level} onChange={e => set('job_level', e.target.value)} className={inputCls} placeholder="如：P6 / 高级工程师" />
            </Field>
            <Field label="年度">
              <input value={form.year} onChange={e => set('year', e.target.value)} className={inputCls} placeholder="如：2026" />
            </Field>
          </div>

          {/* 三级审批 */}
          <SectionTitle icon={Check} text="三级审批" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="一级审批人(HR)" required>
              <select value={form.approver_l1_id} onChange={e => set('approver_l1_id', e.target.value)} className={inputCls}>
                <option value="">请选择</option>
                {approverCandidates.l1.map(u => (
                  <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                ))}
              </select>
            </Field>
            <Field label="二级审批人(业务)" required>
              <select value={form.approver_l2_id} onChange={e => set('approver_l2_id', e.target.value)} className={inputCls}>
                <option value="">请选择</option>
                {approverCandidates.l2.map(u => (
                  <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                ))}
              </select>
            </Field>
            <Field label="三级审批人(CEO)" required>
              <select value={form.approver_l3_id} onChange={e => set('approver_l3_id', e.target.value)} className={inputCls}>
                <option value="">请选择</option>
                {approverCandidates.l3.map(u => (
                  <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="导师"><input value={form.mentor} onChange={e => set('mentor', e.target.value)} className={inputCls} placeholder="导师姓名" /></Field>
            <Field label="汇报对象"><input value={form.report_to} onChange={e => set('report_to', e.target.value)} className={inputCls} placeholder="直属上级" /></Field>
            <Field label="工作地点">
              <input list="hc-loc-list" value={form.work_location} onChange={e => set('work_location', e.target.value)} className={inputCls} placeholder="选择或输入地点" />
              <datalist id="hc-loc-list">{WORK_LOCATION_PRESETS.map(l => <option key={l} value={l} />)}</datalist>
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={`预算成本(元/人·年)${canSeeCost ? '' : ' 选填'}`}>
              <input type="number" value={form.budget_annual} onChange={e => set('budget_annual', e.target.value)} className={inputCls} placeholder="如：450000" />
            </Field>
            <Field label="预期到岗时间">
              <input type="date" value={form.expected_date} onChange={e => set('expected_date', e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* 面试安排 */}
          <SectionTitle icon={Users} text="面试安排" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="一面面试官"><input value={form.interviewer_first} onChange={e => set('interviewer_first', e.target.value)} className={inputCls} placeholder="姓名" /></Field>
            <Field label="二面面试官"><input value={form.interviewer_second} onChange={e => set('interviewer_second', e.target.value)} className={inputCls} placeholder="姓名" /></Field>
            <Field label="终面面试官（如有）"><input value={form.interviewer_final} onChange={e => set('interviewer_final', e.target.value)} className={inputCls} placeholder="姓名" /></Field>
          </div>

          {/* 现有团队文档 */}
          <SectionTitle icon={FileText} text="现有团队文档" />
          <div className="space-y-2">
            <p className="text-xs text-gray-400 -mt-1">可挂多个飞书文档链接（如团队现状、组织架构、业务规划等）</p>
            {teamDocs.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={d.title} onChange={e => setDoc(i, 'title', e.target.value)}
                  className={clsx(inputCls, 'md:w-40 shrink-0')} placeholder="文档名称（选填）" />
                <div className="relative flex-1">
                  <Link2 size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={d.url} onChange={e => setDoc(i, 'url', e.target.value)}
                    className={clsx(inputCls, 'pl-8')} placeholder="https://xxx.feishu.cn/docx/..." />
                </div>
                <button type="button" onClick={() => removeDoc(i)}
                  className="shrink-0 p-2 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50" title="删除">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <button type="button" onClick={addDoc}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700">
              <Plus size={14} />添加文档链接
            </button>
          </div>

          {/* 需求论证 */}
          <SectionTitle icon={AlertCircle} text="需求论证" />
          <Field label="编制需求原因（不招人是否无法解决？）">
            <textarea value={form.reason} onChange={e => set('reason', e.target.value)} rows={3} className={textareaCls} placeholder="说明业务背景、为什么必须新增编制、不招人会带来什么影响..." />
          </Field>
          <Field label="未来 90 天核心交付目标">
            <textarea value={form.goal_90d} onChange={e => set('goal_90d', e.target.value)} rows={3} className={textareaCls} placeholder="该岗位入职后 90 天内需要交付的关键成果..." />
          </Field>
          <Field label="人才画像">
            <textarea value={form.talent_profile} onChange={e => set('talent_profile', e.target.value)} rows={3} className={textareaCls} placeholder="能力要求、经验背景、软性素质等..." />
          </Field>
        </div>
        <div className="sticky bottom-0 bg-white flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">取消</button>
          <button onClick={submit} disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-1.5">
            {submitting && <Loader2 size={14} className="animate-spin" />}提交申请
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <Icon size={15} className="text-indigo-500" />
      <span className="text-sm font-semibold text-gray-700">{text}</span>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

/* ============================ 创建时间范围选择 ============================ */
function fmtDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function DateRangeFilter({ range, onChange }: { range: { from: string; to: string }; onChange: (r: { from: string; to: string }) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const active = !!(range.from || range.to);
  const today = new Date();
  const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
  const yr = today.getFullYear();
  const presets: { label: string; get: () => { from: string; to: string } }[] = [
    { label: '近 7 天', get: () => ({ from: fmtDate(daysAgo(6)), to: fmtDate(today) }) },
    { label: '近 30 天', get: () => ({ from: fmtDate(daysAgo(29)), to: fmtDate(today) }) },
    { label: '近 90 天', get: () => ({ from: fmtDate(daysAgo(89)), to: fmtDate(today) }) },
    { label: '今年', get: () => ({ from: `${yr}-01-01`, to: fmtDate(today) }) },
    { label: '去年', get: () => ({ from: `${yr - 1}-01-01`, to: `${yr - 1}-12-31` }) },
    { label: 'Q1', get: () => ({ from: `${yr}-01-01`, to: `${yr}-03-31` }) },
    { label: 'Q2', get: () => ({ from: `${yr}-04-01`, to: `${yr}-06-30` }) },
    { label: 'Q3', get: () => ({ from: `${yr}-07-01`, to: `${yr}-09-30` }) },
    { label: 'Q4', get: () => ({ from: `${yr}-10-01`, to: `${yr}-12-31` }) },
  ];
  const label = active ? `${range.from || '不限'} ~ ${range.to || '不限'}` : '创建时间';

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className={clsx('h-8 px-3 rounded-lg border text-xs flex items-center gap-1.5 whitespace-nowrap transition-colors',
          active ? 'border-indigo-300 bg-indigo-50 text-indigo-600' : 'border-gray-200 bg-white text-gray-500 hover:border-indigo-300')}>
        <CalendarDays size={14} />
        <span className="max-w-[160px] truncate">{label}</span>
        {active
          ? <X size={13} className="text-gray-400 hover:text-gray-600" onClick={(e) => { e.stopPropagation(); onChange({ from: '', to: '' }); }} />
          : <ChevronDown size={13} className={clsx('text-gray-400 transition-transform', open && 'rotate-180')} />}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-white rounded-xl border border-gray-200 shadow-lg z-20 p-3">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {presets.map(p => {
              const v = p.get();
              const isSel = range.from === v.from && range.to === v.to;
              return (
                <button key={p.label} onClick={() => onChange(v)}
                  className={clsx('px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors',
                    isSel ? 'bg-indigo-600 text-white' : 'bg-gray-50 text-gray-500 hover:bg-indigo-50 hover:text-indigo-600')}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-400 w-8">开始</span>
              <input type="date" value={range.from} max={range.to || undefined}
                onChange={e => onChange({ ...range, from: e.target.value })}
                className="flex-1 h-8 px-2 rounded-lg border border-gray-200 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-400 w-8">结束</span>
              <input type="date" value={range.to} min={range.from || undefined}
                onChange={e => onChange({ ...range, to: e.target.value })}
                className="flex-1 h-8 px-2 rounded-lg border border-gray-200 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
            <button onClick={() => onChange({ from: '', to: '' })} className="text-[11px] text-gray-400 hover:text-gray-600">清除</button>
            <button onClick={() => setOpen(false)} className="text-[11px] px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">确定</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ 年度多选下拉 ============================ */
function YearMultiSelect({ years, onChange }: { years: string[]; onChange: (y: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cur = new Date().getFullYear();
  // 可选年度：近 5 年 + 明年，并确保已选年份都在列表内
  const base: string[] = [];
  for (let y = cur + 1; y >= cur - 4; y--) base.push(String(y));
  const options = Array.from(new Set([...base, ...years])).sort((a, b) => Number(b) - Number(a));

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = (y: string) => {
    onChange(years.includes(y) ? years.filter(x => x !== y) : [...years, y].sort((a, b) => Number(b) - Number(a)));
  };
  const label = years.length === 0 ? '全部年度' : years.length === 1 ? `${years[0]}年` : `已选 ${years.length} 年`;

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="h-9 px-3 rounded-lg border border-gray-200 text-sm bg-white text-gray-600 flex items-center gap-1.5 hover:border-indigo-300 whitespace-nowrap">
        {label}
        <ChevronDown size={14} className={clsx('text-gray-400 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-40 bg-white rounded-xl border border-gray-200 shadow-lg z-20 p-1.5">
          <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-gray-100">
            <button onClick={() => onChange(options.slice())} className="text-[11px] text-indigo-500 hover:underline">全选</button>
            <button onClick={() => onChange([])} className="text-[11px] text-gray-400 hover:underline">清空</button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {options.map(y => {
              const checked = years.includes(y);
              return (
                <button key={y} onClick={() => toggle(y)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  <span className={clsx('w-4 h-4 rounded border flex items-center justify-center shrink-0',
                    checked ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300')}>
                    {checked && <Check size={11} className="text-white" />}
                  </span>
                  {y}年
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ shared UI ============================ */
const inputCls = 'w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300';
const textareaCls = 'w-full min-h-[5.5rem] px-3 py-2 rounded-lg border border-gray-200 text-sm leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300';

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">{label}{required && <span className="text-rose-500"> *</span>}</label>
      {children}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1 min-w-0">
      <span className="text-gray-400 shrink-0">{label}：</span>
      <span className="text-gray-700 truncate">{value}</span>
    </div>
  );
}

function DetailBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2">
      <p className="text-[11px] font-medium text-gray-500 mb-0.5">{label}</p>
      <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{text}</p>
    </div>
  );
}

function Panel({ title, subtitle, icon: Icon, children }: { title: string; subtitle?: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="mb-1">
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><Icon size={16} className="text-indigo-500" />{title}</h3>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function StatusBadge({ status }: { status: HcStatus }) {
  const map: Record<HcStatus, string> = {
    AVAILABLE: 'bg-gray-100 text-gray-500', RECRUITING: 'bg-emerald-50 text-emerald-600',
    RESERVED: 'bg-amber-50 text-amber-600', OCCUPIED: 'bg-blue-50 text-blue-600',
    CLOSED: 'bg-gray-100 text-gray-400',
  };
  return <span className={clsx('px-2 py-0.5 rounded-md text-xs font-medium', map[status])}>{HC_STATUS_LABELS[status] || status}</span>;
}

function Seg({ w, cls }: { w: number; cls: string }) {
  return <div className={clsx('h-full', cls)} style={{ width: `${Math.max(0, Math.min(1, w)) * 100}%` }} />;
}

function Legend({ items }: { items: [string, string][] }) {
  return (
    <div className="flex items-center gap-4 text-[11px] text-gray-500 pt-1">
      {items.map(([l, c]) => <span key={l} className="inline-flex items-center gap-1.5"><i className={clsx('w-2 h-2 rounded-full', c)} />{l}</span>)}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="py-10 text-center text-sm text-gray-400">{text}</div>;
}

function Centered({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={clsx('flex items-center justify-center h-full', className)}>{children}</div>;
}

/* ---------- SVG Line Trend ---------- */
function LineTrend({ data }: { data: { period: string; total: number; used: number; recruiting: number }[] }) {
  const W = 520, H = 200, padL = 30, padB = 24, padT = 10, padR = 10;
  const max = Math.max(1, ...data.map(d => d.total), ...data.map(d => d.used), ...data.map(d => d.recruiting));
  const n = data.length;
  if (n === 0) return <Empty text="暂无趋势数据" />;
  const x = (i: number) => padL + (i * (W - padL - padR)) / Math.max(1, n - 1);
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / max);
  const line = (key: 'total' | 'used' | 'recruiting') => data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(d[key])}`).join(' ');
  const series: [string, string][] = [['total', '#3b82f6'], ['used', '#10b981'], ['recruiting', '#f59e0b']];
  return (
    <div className="mt-2">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-52">
        {[0, 0.25, 0.5, 0.75, 1].map(g => (
          <line key={g} x1={padL} x2={W - padR} y1={padT + (H - padT - padB) * g} y2={padT + (H - padT - padB) * g} stroke="#f1f5f9" strokeWidth={1} />
        ))}
        {series.map(([k, c]) => <path key={k} d={line(k as any)} fill="none" stroke={c} strokeWidth={2} />)}
        {series.map(([k, c]) => data.map((d, i) => <circle key={k + i} cx={x(i)} cy={y((d as any)[k])} r={2.5} fill={c} />))}
        {data.map((d, i) => (n <= 12 || i % 2 === 0) && (
          <text key={i} x={x(i)} y={H - 6} fontSize={9} fill="#94a3b8" textAnchor="middle">{d.period}</text>
        ))}
      </svg>
      <Legend items={[['编制总数', 'bg-blue-500'], ['已使用', 'bg-emerald-500'], ['招聘中', 'bg-amber-500']]} />
    </div>
  );
}

/* ---------- SVG Donut ---------- */
function DonutBlock({ total, data }: { total: number; data: { label: string; count: number }[] }) {
  if (!data || data.length === 0 || total === 0) return <Empty text="暂无数据" />;
  const sum = data.reduce((s, d) => s + d.count, 0) || 1;
  let acc = 0;
  const R = 52, C = 60, sw = 16;
  const circ = 2 * Math.PI * R;
  return (
    <div className="flex items-center gap-4 mt-2">
      <svg viewBox="0 0 120 120" className="w-28 h-28 shrink-0">
        <circle cx={C} cy={C} r={R} fill="none" stroke="#f1f5f9" strokeWidth={sw} />
        {data.map((d, i) => {
          const frac = d.count / sum;
          const dash = `${frac * circ} ${circ}`;
          const offset = -acc * circ;
          acc += frac;
          return <circle key={i} cx={C} cy={C} r={R} fill="none" stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
            strokeWidth={sw} strokeDasharray={dash} strokeDashoffset={offset} transform={`rotate(-90 ${C} ${C})`} />;
        })}
        <text x={C} y={C - 2} fontSize={18} fontWeight="bold" fill="#111827" textAnchor="middle">{sum}</text>
        <text x={C} y={C + 14} fontSize={9} fill="#94a3b8" textAnchor="middle">总计</text>
      </svg>
      <div className="flex-1 space-y-1.5 min-w-0">
        {data.map((d, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-gray-600 truncate">
              <i className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
              {d.label}
            </span>
            <span className="text-gray-400 shrink-0 ml-2">{d.count} ({Math.round(d.count / sum * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function bg(c: string) {
  return ({ blue: 'bg-blue-50', emerald: 'bg-emerald-50', amber: 'bg-amber-50', violet: 'bg-violet-50', rose: 'bg-rose-50' } as any)[c] || 'bg-gray-50';
}
function tx(c: string) {
  return ({ blue: 'text-blue-500', emerald: 'text-emerald-500', amber: 'text-amber-500', violet: 'text-violet-500', rose: 'text-rose-500' } as any)[c] || 'text-gray-500';
}
